import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { HELP_ARTICLES } from '../content/site';

export function HelpIndexPage() {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return null;
    return HELP_ARTICLES.filter(
      (a) => a.title.toLowerCase().includes(q) || a.summary.toLowerCase().includes(q) || a.slug.includes(q),
    );
  }, [query]);

  return (
    <>
      <section className="bg-inverse px-6 pt-28 pb-14 lg:px-10 lg:pt-36 lg:pb-16">
        <div className="mx-auto max-w-[700px] text-center">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-brand/30 bg-brand/20 px-3 py-1.5 text-[0.72rem] font-bold tracking-widest text-brand uppercase">
            Help Centre
          </div>
          <h1 className="font-display mb-5 text-[2.5rem] leading-[1.08] font-extrabold text-inverse-fg lg:text-[3.25rem]">
            How can we help?
          </h1>
          <label className="sr-only" htmlFor="help-search">
            Search articles
          </label>
          <input
            id="help-search"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search articles — try 'pos', 'tax', 'ledger'..."
            className="w-full rounded-xl border border-inverse-fg/[0.12] bg-panel/[0.07] px-5 py-4 text-[0.95rem] text-inverse-fg outline-none placeholder-inverse-fg/30 focus:border-brand/50"
          />
        </div>
      </section>

      <section className="px-6 py-14 lg:px-10">
        <div className="mx-auto max-w-[860px]">
          {filtered ? (
            <>
              <div className="mb-4 text-[0.78rem] font-semibold text-ink-3">
                {filtered.length} result{filtered.length !== 1 ? 's' : ''} for &quot;{query}&quot;
              </div>
              {filtered.length === 0 ? (
                <p className="text-ink-3">
                  No published articles match. <Link to="/contact" className="font-semibold text-brand">Contact support</Link>.
                </p>
              ) : (
                <div className="space-y-2">
                  {filtered.map((a) => (
                    <Link
                      key={a.slug}
                      to={`/help/${a.slug}`}
                      className="block rounded-xl border border-line bg-panel px-4 py-3.5 hover:border-brand/30"
                    >
                      <div className="font-semibold text-ink">{a.title}</div>
                      <div className="mt-0.5 text-[0.8rem] text-ink-3">{a.summary}</div>
                    </Link>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="mk-stagger grid gap-3 sm:grid-cols-2">
              {HELP_ARTICLES.map((a) => (
                <Link
                  key={a.slug}
                  to={`/help/${a.slug}`}
                  className="mk-lift block rounded-2xl border border-line bg-panel p-5 shadow-sm hover:border-brand/30"
                >
                  <h2 className="font-display mb-2 text-[1rem] font-bold text-ink">{a.title}</h2>
                  <p className="text-[0.82rem] text-ink-3">{a.summary}</p>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="border-t border-line bg-surface-2/60 px-6 py-14 lg:px-10">
        <div className="mk-stagger mx-auto grid max-w-[700px] gap-4 sm:grid-cols-3">
          {[
            { label: 'Email support', detail: 'support@cevop.com' },
            { label: 'Sales', detail: 'sales@cevop.com' },
            { label: 'Security', detail: 'security@cevop.com' },
          ].map((item) => (
            <a
              key={item.label}
              href={`mailto:${item.detail}`}
              className="mk-lift rounded-2xl border border-line bg-panel p-5 text-center shadow-sm"
            >
              <div className="font-display mb-1 text-[0.875rem] font-bold text-ink">{item.label}</div>
              <div className="text-[0.75rem] font-medium text-brand">{item.detail}</div>
            </a>
          ))}
        </div>
      </section>
    </>
  );
}
